import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1764923746968-0e79a5d72942?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhZXN0aGV0aWMlMjBib29rc3RvcmUlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODIxNzMzNDh8MA&ixlib=rb-4.1.0&q=85";
const BRAND = "Yaa•Nuwn•u Mah•Kal•aat";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();

  const [siEmail, setSiEmail] = useState("");
  const [siPwd, setSiPwd] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submitSignin = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try { await login(siEmail, siPwd); nav("/"); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-background">
      <div className="hidden lg:block lg:col-span-3 relative overflow-hidden">
        <img src={HERO} alt="bookstore" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/40 to-black/70" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt={BRAND} className="w-12 h-12 object-contain shrink-0" data-testid="login-logo"/>
            <div className="min-w-0">
              <div className="font-display text-xl leading-tight">{BRAND}</div>
              <div className="overline mt-1 text-white/70">Inventory System</div>
            </div>
          </div>
          <div className="max-w-lg space-y-6">
            <h1 className="font-display text-4xl sm:text-5xl leading-[1.05]">Organize, track and monitor — across every store, all in one place.</h1>
            <p className="text-white/80 text-base leading-relaxed">A centralized inventory system designed to help bookstores track, monitor and manage their book inventory with ease. Bookstores can view current stock levels, identify low- or out-of-stock titles, monitor orders and receive important updates when books are restocked or become available. This system helps improve communication, organization and inventory accuracy across all participating bookstores.</p>
          </div>
          <div className="text-xs text-white/60 tracking-widest uppercase">© Nuwaubian Islaamic Hebrews 2026</div>
        </div>
      </div>

      <div className="lg:col-span-2 flex items-center justify-center px-6 py-12 lg:px-12">
        <div className="w-full max-w-sm space-y-6">
          <div className="space-y-2">
            <div className="overline">Welcome</div>
            <h2 className="font-display text-3xl" data-testid="auth-heading">Sign in</h2>
          </div>

          <form onSubmit={submitSignin} className="space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="si-email">Email</Label>
              <Input id="si-email" type="email" value={siEmail} onChange={(e) => setSiEmail(e.target.value)} required data-testid="login-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="si-pwd">Password</Label>
              <Input id="si-pwd" type="password" value={siPwd} onChange={(e) => setSiPwd(e.target.value)} required data-testid="login-password-input" />
            </div>
            {err && <div className="text-sm text-destructive" data-testid="login-error">{err}</div>}
            <Button type="submit" disabled={loading} className="w-full" data-testid="login-submit-button">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              Need an account or a password reset? Contact your administrator.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
